import { useState, useEffect, useCallback } from "react";
import { jsPDF } from "jspdf";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  X, Plus, CalendarBlank, CaretLeft, CaretRight, Sparkle, PaperPlaneTilt, Warning,
  Trash, Clock, Users as UsersIcon, Printer,
} from "@phosphor-icons/react";

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function startOfWeek(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - x.getDay()); return x;
}
function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function hhmm(iso) { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" }); }
function to12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h < 12 ? "am" : "pm";
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, "0")}${ap}` : `${hr}${ap}`;
}

export default function SchedulePanel({ open, onClose, onChanged }) {
  const { user, can } = useAuth();
  const isManager = can("schedule_manage");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [newGuest, setNewGuest] = useState("");
  const [onCallOnly, setOnCallOnly] = useState(false);
  const [timeoff, setTimeoff] = useState([]);
  const [shiftDialog, setShiftDialog] = useState(null); // {employee_id, date} or existing shift
  const [aiOpen, setAiOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewMode, setViewMode] = useState("employee"); // employee | position
  const [storeFilter, setStoreFilter] = useState("");
  const [stores, setStores] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [availMap, setAvailMap] = useState({});
  const [assocTab, setAssocTab] = useState("mine"); // associate: mine | team

  const days = [...Array(7)].map((_, i) => addDays(weekStart, i));
  const weekEnd = ymd(addDays(weekStart, 6));

  const load = useCallback(async () => {
    const params = { start: ymd(weekStart), end: weekEnd };
    if (storeFilter) params.store_id = storeFilter;
    if (!isManager && assocTab === "team") params.team = true;
    const { data } = await api.get("/shifts", { params });
    setShifts(data);
    if (isManager) {
      api.get("/timeoff", { params: { scope: "all", status: "approved" } }).then((r) => setTimeoff(r.data)).catch(() => {});
      api.get("/availability/overview", { params: { start: ymd(weekStart), end: weekEnd } }).then((r) => {
        const m = {}; (r.data || []).forEach((a) => { m[`${a.employee_id}|${a.date}`] = a; }); setAvailMap(m);
      }).catch(() => {});
    }
  }, [weekStart, isManager, storeFilter, assocTab]); // eslint-disable-line

  useEffect(() => {
    if (open) {
      load();
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
      if (isManager) {
        api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
        api.get("/scheduling/guests").then((r) => setGuests(r.data)).catch(() => {});
      }
    }
  }, [open, load]); // eslint-disable-line

  const loadGuests = () => api.get("/scheduling/guests").then((r) => setGuests(r.data)).catch(() => {});
  const addGuest = async () => {
    const name = newGuest.trim();
    if (!name) return;
    try { await api.post("/scheduling/guests", { name }); setNewGuest(""); toast.success(`Added ${name}`); loadGuests(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeGuest = async (g) => {
    if (!window.confirm(`Remove ${g.name} from the schedule? Their shifts will be deleted.`)) return;
    try { await api.delete(`/scheduling/guests/${g.id}`); toast.success(`Removed ${g.name}`); loadGuests(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!open) return null;

  const employees = users.filter((u) => u.role !== "owner");
  const shiftsFor = (eid, d) => shifts.filter((s) => s.employee_id === eid && s.shift_date === ymd(d) && s.shift_status !== "canceled" && (!onCallOnly || s.is_on_call));
  const guestShiftsFor = (gid, d) => shifts.filter((s) => s.guest_id === gid && s.shift_date === ymd(d) && s.shift_status !== "canceled" && (!onCallOnly || s.is_on_call));
  const openShifts = (d) => shifts.filter((s) => !s.employee_id && !s.guest_id && s.shift_date === ymd(d) && s.shift_status !== "canceled" && (!onCallOnly || s.is_on_call));
  const offFor = (eid, d) => timeoff.some((t) => t.employee_id === eid && t.start_datetime.slice(0, 10) <= ymd(d) && ymd(d) <= t.end_datetime.slice(0, 10));
  const availHint = (eid, d) => {
    const a = availMap[`${eid}|${ymd(d)}`];
    if (!a) return null;
    if (!a.available) return { text: "Unavailable", cls: "text-red-500/70" };
    if (a.preset === "custom") return { text: `Custom · ${a.start_time || ""}–${a.end_time || ""}`, cls: "text-black/45" };
    const names = { any: "Any", morning: "Morning", evening: "Closing" };
    const s = a.start_time ? to12(a.start_time) : "Open";
    const e = a.end_time ? to12(a.end_time) : "Close";
    const range = `${s}–${e}`;
    return { text: names[a.preset] ? `${names[a.preset]} · ${range}` : range, cls: "text-black/45" };
  };

  const publishWeek = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shifts/publish", { week_start: ymd(weekStart), week_end: weekEnd });
      toast.success(`Published ${data.published} shift(s)` + (data.skipped.length ? ` · ${data.skipped.length} skipped (conflicts)` : ""));
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const clearWeek = async () => {
    if (!window.confirm(`Clear the ENTIRE week of ${ymd(weekStart)}? This permanently removes ALL shifts (drafts and published) for this week. Assigned employees will be notified.`)) return;
    setBusy(true);
    try {
      const body = { week_start: ymd(weekStart), week_end: weekEnd };
      if (storeFilter) body.store_id = storeFilter;
      const { data } = await api.post("/shifts/clear-week", body);
      toast.success(`Cleared ${data.removed} shift(s) for the week`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const reassign = async (shiftId, assignment, newDate) => {
    const s = shifts.find((x) => x.id === shiftId);
    if (!s) return;
    const payload = { ...assignment };
    if (newDate && newDate !== s.shift_date) {
      payload.shift_date = newDate;
      payload.start_datetime = newDate + s.start_datetime.slice(10);
      payload.end_datetime = newDate + s.end_datetime.slice(10);
    }
    const sameEmp = "employee_id" in assignment && assignment.employee_id === s.employee_id;
    const sameGuest = "guest_id" in assignment && assignment.guest_id === s.guest_id;
    if ((sameEmp || sameGuest) && !payload.shift_date) return;
    try { await api.put(`/shifts/${shiftId}`, payload); toast.success("Shift moved"); load(); onChanged?.(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d && typeof d === "object" && d.conflicts ? d.message + " " + d.conflicts.map((c) => c.conflict_description).join(" ") : formatApiError(d));
    }
  };

  // Build a real PDF client-side (works on desktop + mobile PWA; no window.open / print dialog,
  // which iOS standalone blocks — that was bouncing users to the home screen).
  const buildSchedulePdf = () => {
    // US Letter LANDSCAPE (11 x 8.5 in = 792 x 612 pt). The table is scaled to fill the full
    // printable width AND height with minimal margins, so it prints edge-to-edge from iOS
    // AirPrint without leaving blank space or needing manual orientation/scaling.
    const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 18;          // 0.25"
    const titleH = 24;
    const nameW = 116;
    const dayW = (pageW - margin * 2 - nameW) / 7;
    const headerH = 22;
    const label = `${weekStart.toLocaleDateString()} – ${addDays(weekStart, 6).toLocaleDateString()}`;

    const cellFor = (list, off) => {
      if (off) return ["TIME OFF"];
      if (!list.length) return ["—"];
      return list.map((s) => `${hhmm(s.start_datetime)}–${hhmm(s.end_datetime)}${s.position ? " " + s.position : ""}${s.is_on_call ? " [ON CALL]" : ""}`);
    };
    const rows = [];
    employees.forEach((emp) => rows.push({ name: emp.name, cells: days.map((d) => cellFor(shiftsFor(emp.id, d), offFor(emp.id, d))) }));
    guests.forEach((g) => rows.push({ name: `${g.name} (guest)`, cells: days.map((d) => cellFor(guestShiftsFor(g.id, d), false)) }));
    if (rows.length === 0) rows.push({ name: "—", cells: days.map(() => ["—"]) });

    const tableTop = margin + titleH;
    const avail = pageH - margin - tableTop - headerH; // height for data rows
    const minRowH = 26, maxRowH = 120, fs = 8;
    const rowsPerPage = Math.max(1, Math.floor(avail / minRowH));
    const pages = Math.ceil(rows.length / rowsPerPage);
    const rowH = pages > 1 ? avail / rowsPerPage : Math.min(maxRowH, avail / rows.length);

    const drawTextBlock = (lines, x, w, top, h, bold, size, color) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size); doc.setTextColor(color);
      const lh = size + 2;
      let ty = top + (h - lines.length * lh) / 2 + (size - 1);
      lines.forEach((ln) => { doc.text(ln, x + 4, ty); ty += lh; });
    };
    const drawHeaderRow = (top) => {
      doc.setDrawColor(0); doc.setLineWidth(0.5); doc.setFillColor(230, 230, 230);
      doc.rect(margin, top, nameW, headerH, "FD");
      days.forEach((d, i) => doc.rect(margin + nameW + i * dayW, top, dayW, headerH, "FD"));
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(0);
      doc.text("Employee", margin + 4, top + 15);
      days.forEach((d, i) => doc.text(`${DAY_LABELS[d.getDay()]} ${d.getDate()}`, margin + nameW + i * dayW + 4, top + 15));
    };

    for (let p = 0; p < pages; p++) {
      if (p > 0) doc.addPage();
      doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(0);
      doc.text("Weekly Schedule", margin, margin + 14);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
      doc.text(label + (pages > 1 ? `   (page ${p + 1} of ${pages})` : ""), margin + 150, margin + 14);
      drawHeaderRow(tableTop);
      let y = tableTop + headerH;
      rows.slice(p * rowsPerPage, (p + 1) * rowsPerPage).forEach((row) => {
        doc.setDrawColor(0); doc.setLineWidth(0.5);
        doc.rect(margin, y, nameW, rowH);
        doc.setFontSize(9);
        drawTextBlock(doc.splitTextToSize(row.name, nameW - 8), margin, nameW, y, rowH, true, 9, 0);
        days.forEach((d, i) => {
          const x = margin + nameW + i * dayW;
          doc.rect(x, y, dayW, rowH);
          const wrapped = [];
          doc.setFontSize(fs);
          row.cells[i].forEach((ln) => doc.splitTextToSize(ln, dayW - 8).forEach((w) => wrapped.push(w)));
          drawTextBlock(wrapped, x, dayW, y, rowH, false, fs, 20);
        });
        y += rowH;
      });
    }
    return doc;
  };

  const printSchedule = () => {
    try {
      buildSchedulePdf().save(`schedule-${ymd(weekStart)}-to-${ymd(addDays(weekStart, 6))}.pdf`);
      toast.success("Schedule PDF ready — open it to print or share.");
    } catch (e) {
      toast.error("Couldn't generate the schedule PDF. Please try again.");
    }
  };

  const positions = Array.from(new Set(shifts.filter((s) => s.position).map((s) => s.position)));

  // ---- Associate view ----
  if (!isManager) {
    const mine = shifts.filter((s) => s.employee_id === user.id && s.shift_status !== "canceled" && s.shift_status !== "declined").sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));
    const claim = async (s) => { try { await api.post(`/shifts/${s.id}/claim`); toast.success("Open shift claimed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
    const open = shifts.filter((s) => !s.employee_id && !s.guest_id);
    const teamEmployees = employees.filter((e) => shifts.some((s) => s.employee_id === e.id));
    const teamGuests = Array.from(new Map(shifts.filter((s) => s.guest_id).map((s) => [s.guest_id, s.guest_name || "Guest"])).entries()).map(([id, name]) => ({ id, name }));
    const guestTeamShiftsFor = (gid, d) => shifts.filter((s) => s.guest_id === gid && s.shift_date === ymd(d) && s.shift_status !== "canceled");

    const tabs = (
      <div className="flex rounded-full border-2 border-black overflow-hidden bg-white">
        <button data-testid="assoc-tab-mine" onClick={() => setAssocTab("mine")} className={`px-3 h-9 text-xs font-bold ${assocTab === "mine" ? "bg-primary text-white" : ""}`}>My Schedule</button>
        <button data-testid="assoc-tab-team" onClick={() => setAssocTab("team")} className={`px-3 h-9 text-xs font-bold ${assocTab === "team" ? "bg-primary text-white" : ""}`}>Team Calendar</button>
      </div>
    );

    if (assocTab === "team") {
      return (
        <Drawer onClose={onClose} weekStart={weekStart} setWeekStart={setWeekStart} title="Team Schedule" wide actions={tabs}>
          <div className="p-3 overflow-x-auto">
            {teamEmployees.length === 0 && teamGuests.length === 0 && open.length === 0 ? <Empty /> : (
              <div className="min-w-[760px]" data-testid="team-calendar-grid">
                <div className="grid" style={{ gridTemplateColumns: "140px repeat(7, 1fr)" }}>
                  <div className="sticky left-0 z-[5] bg-background p-2 text-[11px] font-bold uppercase tracking-wide text-black/50">Employee</div>
                  {days.map((d) => (
                    <div key={ymd(d)} className="p-2 text-center">
                      <p className="text-[11px] font-bold">{DAY_LABELS[d.getDay()]}</p>
                      <p className="text-[11px] text-black/50">{d.getDate()}</p>
                    </div>
                  ))}
                </div>
                {open.length > 0 && (
                  <div className="grid border-t-2 border-black" style={{ gridTemplateColumns: "140px repeat(7, 1fr)" }}>
                    <div className="sticky left-0 z-[5] bg-background p-2 text-sm font-bold">Open</div>
                    {days.map((d) => (
                      <div key={ymd(d)} className="p-1 border-l border-black/10 space-y-1">
                        {openShifts(d).map((s) => (
                          <div key={s.id} className="text-[10px] rounded-md border border-dashed border-black bg-[#F3E8FF] px-1.5 py-1 leading-tight">
                            {hhmm(s.start_datetime)}–{hhmm(s.end_datetime)}{s.position && <span className="block text-black/50">{s.position}</span>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {teamEmployees.map((emp) => (
                  <div key={emp.id} className="grid border-t border-black/15" style={{ gridTemplateColumns: "140px repeat(7, 1fr)" }}>
                    <div className="sticky left-0 z-[5] bg-background p-2 text-sm font-semibold truncate">{emp.name}{emp.id === user.id && <span className="text-[10px] text-primary font-bold"> (you)</span>}</div>
                    {days.map((d) => (
                      <div key={ymd(d)} className={`p-1 border-l border-black/10 space-y-1 ${emp.id === user.id ? "bg-primary/5" : ""}`}>
                        {shiftsFor(emp.id, d).map((s) => (
                          <div key={s.id} data-testid={`team-shift-${s.id}`} className={`text-[10px] rounded-md border border-black px-1.5 py-1 leading-tight ${s.is_on_call ? "bg-[#FED7AA]" : "bg-[#CCFBF1]"}`}>
                            {hhmm(s.start_datetime)}–{hhmm(s.end_datetime)}{s.position && <span className="block text-black/50">{s.position}</span>}
                            {s.is_on_call && <span className="mt-0.5 inline-block px-1 rounded bg-[#EA580C] text-white text-[8px] font-black">ON CALL</span>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
                {teamGuests.map((g) => (
                  <div key={g.id} className="grid border-t border-black/15" style={{ gridTemplateColumns: "140px repeat(7, 1fr)" }}>
                    <div className="sticky left-0 z-[5] bg-background p-2 text-sm font-semibold truncate flex items-center gap-1.5"><span className="truncate">{g.name}</span><span className="text-[8px] font-bold px-1 py-0.5 rounded bg-[#F3E8FF] border border-black shrink-0">GUEST</span></div>
                    {days.map((d) => (
                      <div key={ymd(d)} className="p-1 border-l border-black/10 space-y-1">
                        {guestTeamShiftsFor(g.id, d).map((s) => (
                          <div key={s.id} className={`text-[10px] rounded-md border border-black px-1.5 py-1 leading-tight ${s.is_on_call ? "bg-[#FED7AA]" : "bg-[#EDE9FE]"}`}>
                            {hhmm(s.start_datetime)}–{hhmm(s.end_datetime)}{s.position && <span className="block text-black/50">{s.position}</span>}
                            {s.is_on_call && <span className="mt-0.5 inline-block px-1 rounded bg-[#EA580C] text-white text-[8px] font-black">ON CALL</span>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Drawer>
      );
    }

    return (
      <Drawer onClose={onClose} weekStart={weekStart} setWeekStart={setWeekStart} title="My Schedule" actions={tabs}>
        <div className="p-5 space-y-3">
          {mine.length === 0 && open.length === 0 && <Empty />}
          {mine.map((s) => (
            <div key={s.id} data-testid={`myshift-${s.id}`} className={`rounded-xl border-2 border-black p-4 hard-shadow-sm flex items-center justify-between gap-3 ${s.is_on_call ? "bg-[#FFF7ED]" : "bg-white"}`}>
              <div>
                <p className="font-bold">{new Date(s.shift_date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" })}</p>
                <p className="text-sm text-black/60 flex items-center gap-1"><Clock size={13} /> {hhmm(s.start_datetime)} – {hhmm(s.end_datetime)}{s.position ? ` · ${s.position}` : ""}</p>
              </div>
              <span data-testid={`myshift-badge-${s.id}`} className={`text-[10px] font-bold px-2 py-1 rounded-full border-2 border-black ${s.is_on_call ? "bg-[#EA580C] text-white" : "bg-[#CCFBF1]"}`}>{s.is_on_call ? "On Call" : "Scheduled"}</span>
            </div>
          ))}
          {open.length > 0 && <p className={labelCls + " pt-2"}>Open shifts you can claim</p>}
          {open.map((s) => (
            <div key={s.id} data-testid={`openshift-${s.id}`} className="rounded-xl border-2 border-dashed border-black bg-[#F3E8FF] p-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-bold">{new Date(s.shift_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })}</p>
                <p className="text-sm text-black/60 flex items-center gap-1"><Clock size={13} /> {hhmm(s.start_datetime)} – {hhmm(s.end_datetime)}</p>
              </div>
              <button data-testid={`claim-shift-${s.id}`} onClick={() => claim(s)} className="press h-9 px-3 rounded-full bg-primary text-white text-xs font-bold border-2 border-black">Claim</button>
            </div>
          ))}
        </div>
      </Drawer>
    );
  }

  // ---- Manager grid view ----
  const empView = viewMode === "employee";
  return (
    <Drawer onClose={onClose} weekStart={weekStart} setWeekStart={setWeekStart} title="Schedule" wide
      actions={
        <>
          <div className="flex rounded-full border-2 border-black overflow-hidden bg-white">
            <button data-testid="view-employee" onClick={() => setViewMode("employee")} className={`h-9 px-3 text-xs font-bold ${empView ? "bg-black text-white" : ""}`}>By employee</button>
            <button data-testid="view-position" onClick={() => setViewMode("position")} className={`h-9 px-3 text-xs font-bold border-l-2 border-black ${!empView ? "bg-black text-white" : ""}`}>By position</button>
          </div>
          {stores.length > 1 && (
            <select data-testid="store-filter" value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className="h-9 px-2 rounded-full border-2 border-black bg-white text-xs font-bold">
              <option value="">All stores</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <button data-testid="print-schedule-btn" onClick={printSchedule} className="press h-9 px-3 rounded-full bg-white text-xs font-bold border-2 border-black flex items-center gap-1"><Printer size={14} weight="bold" /> Save PDF</button>
          <button data-testid="oncall-filter-btn" onClick={() => setOnCallOnly((v) => !v)} className={`press h-9 px-3 rounded-full text-xs font-bold border-2 border-black flex items-center gap-1 ${onCallOnly ? "bg-[#EA580C] text-white" : "bg-white"}`}>On Call only</button>
          {can("use_ai_scheduling_suggestions") && <button data-testid="ai-build-btn" onClick={() => setAiOpen(true)} className="press h-9 px-3 rounded-full bg-secondary text-white text-xs font-bold border-2 border-black flex items-center gap-1"><Sparkle size={14} weight="fill" /> Build with AI</button>}
          {can("schedule_publish") && <button data-testid="publish-week-btn" disabled={busy} onClick={publishWeek} className="press h-9 px-3 rounded-full bg-primary text-white text-xs font-bold border-2 border-black flex items-center gap-1"><PaperPlaneTilt size={14} weight="fill" /> Publish week</button>}
          {user?.role === "owner" && <button data-testid="clear-week-btn" disabled={busy} onClick={clearWeek} className="press h-9 px-3 rounded-full bg-[#FEE2E2] text-red-600 text-xs font-bold border-2 border-black flex items-center gap-1"><Trash size={14} weight="bold" /> Clear week</button>}
        </>
      }>
      <div className="p-4 overflow-x-auto">
        <div className="min-w-[980px]">
          <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: "150px repeat(7,1fr)" }}>
            <div className="sticky left-0 z-[5] bg-background text-xs font-bold uppercase text-black/50 px-2 py-2">{empView ? "Employee" : "Position"}</div>
            {days.map((d, i) => (
              <div key={i} className="text-center px-1 py-2 rounded-lg bg-black/5">
                <p className="text-xs font-bold">{DAY_LABELS[d.getDay()]}</p>
                <p className="text-[11px] text-black/50">{d.getDate()}</p>
              </div>
            ))}
          </div>

          {empView ? (
            <>
              {/* Open shifts row */}
              <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: "150px repeat(7,1fr)" }}>
                <div className="sticky left-0 z-[5] bg-background px-2 py-2 text-sm font-bold flex items-center gap-1 text-secondary"><UsersIcon size={14} weight="bold" /> Open</div>
                {days.map((d, i) => (
                  <div key={i} onDragOver={(e) => e.preventDefault()} onDrop={() => dragId && reassign(dragId, { employee_id: null, guest_id: null }, ymd(d))} className="min-h-[52px] rounded-lg border-2 border-dashed border-black/20 p-1 space-y-1">
                    {openShifts(d).map((s) => (
                      <ShiftChip key={s.id} s={s} onClick={() => setShiftDialog(s)} open draggable onDragStart={() => setDragId(s.id)} onDragEnd={() => setDragId(null)} />
                    ))}
                    <button data-testid={`add-open-${ymd(d)}`} onClick={() => setShiftDialog({ employee_id: null, shift_date: ymd(d) })} className="w-full text-[10px] text-black/40 hover:text-black">+ open</button>
                  </div>
                ))}
              </div>
              {employees.map((emp) => (
                <div key={emp.id} className="grid gap-1 mb-1" style={{ gridTemplateColumns: "150px repeat(7,1fr)" }}>
                  <div className="sticky left-0 z-[5] bg-background px-2 py-2 text-sm font-bold truncate flex items-center gap-1">{emp.name}</div>
                  {days.map((d, i) => {
                    const off = offFor(emp.id, d);
                    return (
                      <div key={i} data-testid={`cell-${emp.id}-${ymd(d)}`} onDragOver={(e) => !off && e.preventDefault()} onDrop={() => !off && dragId && reassign(dragId, { employee_id: emp.id }, ymd(d))} className={`min-h-[52px] rounded-lg border-2 p-1 space-y-1 ${off ? "border-black bg-[#FEE2E2]" : dragId ? "border-primary/40 border-dashed" : "border-black/15"}`}>
                        {off && <span className="text-[9px] font-bold px-1 rounded bg-black text-white">TIME OFF</span>}
                        {!off && (() => { const h = availHint(emp.id, d); return h ? <span className={`block text-[9px] font-bold ${h.cls}`}>{h.text}</span> : null; })()}
                        {shiftsFor(emp.id, d).map((s) => (
                          <ShiftChip key={s.id} s={s} onClick={() => setShiftDialog(s)} draggable onDragStart={() => setDragId(s.id)} onDragEnd={() => setDragId(null)} />
                        ))}
                        {!off && <button data-testid={`add-shift-${emp.id}-${ymd(d)}`} onClick={() => setShiftDialog({ employee_id: emp.id, shift_date: ymd(d) })} className="w-full text-[10px] text-black/30 hover:text-black">+</button>}
                      </div>
                    );
                  })}
                </div>
              ))}
              {/* Guest (non-employee) rows */}
              {guests.map((g) => (
                <div key={g.id} data-testid={`guest-row-${g.id}`} className="grid gap-1 mb-1" style={{ gridTemplateColumns: "150px repeat(7,1fr)" }}>
                  <div className="sticky left-0 z-[5] bg-background px-2 py-2 text-sm font-bold truncate flex items-center gap-1.5">
                    <span className="truncate">{g.name}</span>
                    <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-[#F3E8FF] border border-black shrink-0">GUEST</span>
                    <button data-testid={`guest-remove-${g.id}`} onClick={() => removeGuest(g)} className="press ml-auto text-black/30 hover:text-red-600 shrink-0"><Trash size={12} weight="bold" /></button>
                  </div>
                  {days.map((d, i) => (
                    <div key={i} data-testid={`guest-cell-${g.id}-${ymd(d)}`} onDragOver={(e) => e.preventDefault()} onDrop={() => dragId && reassign(dragId, { guest_id: g.id }, ymd(d))} className={`min-h-[52px] rounded-lg border-2 p-1 space-y-1 ${dragId ? "border-primary/40 border-dashed" : "border-black/15"}`}>
                      {guestShiftsFor(g.id, d).map((s) => (
                        <ShiftChip key={s.id} s={s} onClick={() => setShiftDialog(s)} draggable onDragStart={() => setDragId(s.id)} onDragEnd={() => setDragId(null)} />
                      ))}
                      <button data-testid={`add-guest-shift-${g.id}-${ymd(d)}`} onClick={() => setShiftDialog({ guest_id: g.id, shift_date: ymd(d) })} className="w-full text-[10px] text-black/30 hover:text-black">+</button>
                    </div>
                  ))}
                </div>
              ))}
              {/* Add a non-employee guest */}
              <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: "150px repeat(7,1fr)" }}>
                <div className="sticky left-0 z-[5] bg-background px-2 py-1 flex items-center gap-1">
                  <input data-testid="new-guest-input" value={newGuest} onChange={(e) => setNewGuest(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGuest()} placeholder="Add a guest…" className="w-full h-8 px-2 rounded-lg border-2 border-dashed border-black/40 bg-white text-xs outline-none focus:ring-2 focus:ring-primary" />
                  <button data-testid="add-guest-btn" onClick={addGuest} className="press w-8 h-8 rounded-lg border-2 border-black bg-white flex items-center justify-center shrink-0"><Plus size={14} weight="bold" /></button>
                </div>
                <div className="col-span-7 px-2 py-1 flex items-center text-[10px] text-black/40">Add a temp, contractor, or helper who has no login — you'll manage their shifts here.</div>
              </div>
            </>
          ) : (
            <>
              {(positions.length ? positions : ["(no positions set)"]).map((pos) => (
                <div key={pos} className="grid gap-1 mb-1" style={{ gridTemplateColumns: "150px repeat(7,1fr)" }}>
                  <div className="sticky left-0 z-[5] bg-background px-2 py-2 text-sm font-bold truncate">{pos}</div>
                  {days.map((d, i) => {
                    const list = shifts.filter((s) => s.shift_date === ymd(d) && s.shift_status !== "canceled" && (s.position || "(no positions set)") === pos && (!onCallOnly || s.is_on_call));
                    return (
                      <div key={i} className="min-h-[52px] rounded-lg border-2 border-black/15 p-1 space-y-1">
                        {list.map((s) => (
                          <button key={s.id} data-testid={`pos-chip-${s.id}`} onClick={() => setShiftDialog(s)} className={`w-full text-left rounded px-1.5 py-1 border-2 border-black text-[10px] font-bold leading-tight ${!s.employee_id && !s.guest_id ? "bg-[#F3E8FF]" : s.is_on_call ? "bg-[#FED7AA]" : s.publish_status === "published" ? "bg-[#DCFCE7]" : "bg-[#FEF9C3]"}`}>
                            <span className="block truncate">{s.employee_name || s.guest_name || "Open"}</span>
                            <span className="block text-black/50">{hhmm(s.start_datetime)}–{hhmm(s.end_datetime)}</span>
                            {s.is_on_call && <span data-testid={`pos-oncall-badge-${s.id}`} className="mt-0.5 inline-block px-1 rounded bg-[#EA580C] text-white text-[8px] font-black">ON CALL</span>}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {shiftDialog && <ShiftDialog data={shiftDialog} employees={employees} guests={guests} onClose={() => setShiftDialog(null)} onSaved={() => { setShiftDialog(null); load(); onChanged?.(); }} />}
      {aiOpen && <AiBuildDialog weekStart={ymd(weekStart)} onClose={() => setAiOpen(false)} onApplied={() => { setAiOpen(false); load(); onChanged?.(); }} />}
    </Drawer>
  );
}

function Drawer({ children, onClose, title, weekStart, setWeekStart, actions, wide }) {
  const end = addDays(weekStart, 6);
  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="schedule-panel" onClick={(e) => e.stopPropagation()} className={`relative bg-background border-l-2 border-black w-full ${wide ? "max-w-5xl" : "max-w-lg"} h-full overflow-y-auto`}>
        <div className="sticky top-0 bg-[#E0F2FE] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center"><CalendarBlank size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <div className="flex items-center gap-1 bg-white rounded-full border-2 border-black px-1">
              <button data-testid="week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))} className="press w-7 h-7 flex items-center justify-center"><CaretLeft size={14} weight="bold" /></button>
              <span className="text-xs font-bold px-1 whitespace-nowrap">{weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – {end.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              <button data-testid="week-next" onClick={() => setWeekStart(addDays(weekStart, 7))} className="press w-7 h-7 flex items-center justify-center"><CaretRight size={14} weight="bold" /></button>
            </div>
            <button data-testid="schedule-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function ShiftChip({ s, onClick, open, draggable, onDragStart, onDragEnd }) {
  return (
    <button data-testid={`shift-chip-${s.id}`} onClick={onClick} draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined} onDragEnd={draggable ? onDragEnd : undefined}
      className={`w-full text-left rounded px-1.5 py-1 border-2 border-black text-[10px] font-bold leading-tight ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${open ? "bg-[#F3E8FF]" : s.is_on_call ? "bg-[#FED7AA]" : s.publish_status === "published" ? "bg-[#DCFCE7]" : "bg-[#FEF9C3]"}`}>
      <span className="flex items-center gap-1">{hhmm(s.start_datetime)}–{hhmm(s.end_datetime)}{s.has_conflict && <Warning size={10} weight="fill" className="text-red-600" />}</span>
      {s.position && <span className="block text-black/50 truncate">{s.position}</span>}
      {s.is_on_call && <span data-testid={`shift-oncall-badge-${s.id}`} className="mt-0.5 inline-block px-1 py-px rounded bg-[#EA580C] text-white text-[8px] font-black tracking-wide">ON CALL</span>}
    </button>
  );
}

function Empty() {
  return (
    <div className="border-2 border-dashed border-black/30 rounded-xl p-10 text-center">
      <CalendarBlank size={36} className="mx-auto text-black/30" />
      <p className="font-display text-lg font-bold mt-2">No shifts this week</p>
    </div>
  );
}

function ShiftDialog({ data, employees, guests = [], onClose, onSaved }) {
  const existing = !!data.id;
  const [form, setForm] = useState({
    assignee: data.guest_id ? `g:${data.guest_id}` : (data.employee_id || ""),
    shift_date: data.shift_date || (data.start_datetime ? data.start_datetime.slice(0, 10) : ""),
    start: data.start_datetime ? new Date(data.start_datetime).toISOString().slice(11, 16) : "09:00",
    end: data.end_datetime ? new Date(data.end_datetime).toISOString().slice(11, 16) : "17:00",
    position: data.position || "",
    manager_notes: data.manager_notes || "",
    is_on_call: !!data.is_on_call,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const isGuest = form.assignee.startsWith("g:");
    const payload = {
      employee_id: isGuest ? null : (form.assignee || null),
      guest_id: isGuest ? form.assignee.slice(2) : null,
      shift_date: form.shift_date,
      start_datetime: `${form.shift_date}T${form.start}:00+00:00`,
      end_datetime: `${form.shift_date}T${form.end}:00+00:00`,
      position: form.position,
      manager_notes: form.manager_notes,
      is_on_call: form.is_on_call,
    };
    try {
      if (existing) await api.put(`/shifts/${data.id}`, payload);
      else await api.post("/shifts", payload);
      toast.success(existing ? "Shift updated" : "Shift created");
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.conflicts) {
        toast.error(d.message + " " + d.conflicts.map((c) => c.conflict_description).join(" "));
      } else toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this shift?")) return;
    try { await api.delete(`/shifts/${data.id}`); toast.success("Shift deleted"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div data-testid="shift-dialog" onClick={(e) => e.stopPropagation()} className="bg-background border-2 border-black rounded-2xl hard-shadow w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">{existing ? "Edit shift" : "New shift"}</h3>
          {existing && <button data-testid="shift-delete-btn" onClick={del} className="press w-8 h-8 rounded-full border-2 border-black flex items-center justify-center bg-white"><Trash size={13} weight="bold" /></button>}
        </div>
        <div>
          <label className={labelCls}>Assign to</label>
          <select data-testid="shift-employee-select" className={inputCls} value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })}>
            <option value="">Open shift (unassigned)</option>
            <optgroup label="Employees">
              {employees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </optgroup>
            {guests.length > 0 && (
              <optgroup label="Guests (non-employees)">
                {guests.map((g) => <option key={g.id} value={`g:${g.id}`}>{g.name} (guest)</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div><label className={labelCls}>Date</label><input data-testid="shift-date-input" type="date" className={inputCls} value={form.shift_date} onChange={(e) => setForm({ ...form, shift_date: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Start</label><input data-testid="shift-start-input" type="time" className={inputCls} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
          <div><label className={labelCls}>End</label><input data-testid="shift-end-input" type="time" className={inputCls} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
        </div>
        <div><label className={labelCls}>Position</label><input data-testid="shift-position-input" className={inputCls} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="e.g. Cashier, Floor" /></div>
        <div><label className={labelCls}>Manager notes</label><input className={inputCls} value={form.manager_notes} onChange={(e) => setForm({ ...form, manager_notes: e.target.value })} /></div>
        <label data-testid="shift-oncall-checkbox" className="flex items-center gap-2 p-3 rounded-lg border-2 border-black bg-[#FFF7ED] cursor-pointer select-none">
          <input type="checkbox" checked={form.is_on_call} onChange={(e) => setForm({ ...form, is_on_call: e.target.checked })} className="w-4 h-4 accent-[#EA580C]" />
          <span className="text-sm font-bold">On Call</span>
          <span className="text-[11px] text-black/50">Mark this shift as on-call (shown in orange with an On Call badge).</span>
        </label>
        <button data-testid="shift-save-btn" onClick={save} disabled={busy} className="press w-full h-11 rounded-full bg-primary text-white font-bold border-2 border-black disabled:opacity-60">{busy ? "Saving…" : existing ? "Save shift" : "Create shift"}</button>
      </div>
    </div>
  );
}

function AiBuildDialog({ weekStart, onClose, onApplied }) {
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ store_id: "", business_hours_start: "09:00", business_hours_end: "18:00", min_employees_per_shift: 1, max_hours_per_employee: 40, notes: "" });
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/stores").then((r) => { setStores(r.data); if (r.data[0]) setForm((f) => ({ ...f, store_id: r.data[0].id })); }).catch(() => {}); }, []);

  const build = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/scheduling/ai-build", { ...form, week_start: weekStart });
      setProposal(data);
      if (!data.proposed_shifts.length) toast.info("AI proposed no shifts — check availability and time off.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try { const { data } = await api.post("/scheduling/ai-apply", { shifts: proposal.proposed_shifts }); toast.success(`Created ${data.created} draft shift(s) — review then publish`); onApplied(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div data-testid="ai-build-dialog" onClick={(e) => e.stopPropagation()} className="bg-background border-2 border-black rounded-2xl hard-shadow w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <h3 className="font-display text-lg font-bold flex items-center gap-2"><Sparkle size={18} weight="fill" /> Build schedule with AI</h3>
        {!proposal ? (
          <>
            <p className="text-sm text-black/60">AI creates a <b>draft</b> only. It never publishes and always skips approved time off.</p>
            <div><label className={labelCls}>Store</label><select className={inputCls} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })}>{stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Opens</label><input type="time" className={inputCls} value={form.business_hours_start} onChange={(e) => setForm({ ...form, business_hours_start: e.target.value })} /></div>
              <div><label className={labelCls}>Closes</label><input type="time" className={inputCls} value={form.business_hours_end} onChange={(e) => setForm({ ...form, business_hours_end: e.target.value })} /></div>
              <div><label className={labelCls}>Min / shift</label><input type="number" min="1" className={inputCls} value={form.min_employees_per_shift} onChange={(e) => setForm({ ...form, min_employees_per_shift: parseInt(e.target.value) || 1 })} /></div>
              <div><label className={labelCls}>Max hrs / employee</label><input type="number" className={inputCls} value={form.max_hours_per_employee} onChange={(e) => setForm({ ...form, max_hours_per_employee: parseInt(e.target.value) || 40 })} /></div>
            </div>
            <div><label className={labelCls}>Notes for AI</label><textarea className={inputCls + " h-20 py-2 resize-none"} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. two associates for opening, one for close" /></div>
            <button data-testid="ai-build-generate" onClick={build} disabled={busy} className="press w-full h-11 rounded-full bg-secondary text-white font-bold border-2 border-black disabled:opacity-60 flex items-center justify-center gap-2"><Sparkle size={16} weight="fill" /> {busy ? "Generating…" : "Generate draft"}</button>
          </>
        ) : (
          <>
            <p className="text-sm font-bold">{proposal.proposed_shifts.length} proposed shift(s)</p>
            <div className="space-y-1 max-h-52 overflow-y-auto">
              {proposal.proposed_shifts.map((s, i) => (
                <div key={i} data-testid={`ai-proposed-${i}`} className="rounded-lg border-2 border-black bg-white p-2 text-xs flex justify-between">
                  <span className="font-bold">{s.employee_name}</span>
                  <span>{s.shift_date} · {s.start_datetime.slice(11, 16)}–{s.end_datetime.slice(11, 16)}{s.position ? ` · ${s.position}` : ""}</span>
                </div>
              ))}
            </div>
            {proposal.exclusions?.length > 0 && (
              <div className="rounded-lg border-2 border-black bg-[#FEF9C3] p-2 text-xs">
                <p className="font-bold mb-1">Not scheduled:</p>
                {proposal.exclusions.map((e, i) => <p key={i}>• {e.employee_name}: {e.reason}</p>)}
              </div>
            )}
            {proposal.notes && <p className="text-xs text-black/60">{proposal.notes}</p>}
            <div className="flex gap-2">
              <button data-testid="ai-build-apply" onClick={apply} disabled={busy || !proposal.proposed_shifts.length} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black disabled:opacity-60">Create draft shifts</button>
              <button onClick={() => setProposal(null)} className="press h-11 px-4 rounded-full bg-white font-bold border-2 border-black">Back</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
